import { queryOptions, skipToken } from "@tanstack/react-query";

import { apiGet } from "@softmax/observatory-client/client";
import type {
  FirstPlaceSummaryPublic,
  GamePublic,
  LeaderboardEntryPublic,
  LeaguePublic,
  RoundDetailPublic,
  RoundEpisodePage,
} from "@softmax/observatory-client/types";
import {
  roundEpisodesPath,
  type RoundEpisodePageQuery,
} from "@/lib/leagues/round-episodes";

export const navigationCacheTimeMs = 3000;

export function leagueCatalogQueryOptions() {
  const elevatedPrivileges = false;
  return queryOptions({
    queryKey: ["leagues", "catalog", { elevatedPrivileges }] as const,
    queryFn: async ({ signal }) => {
      const leagues = await apiGet<LeaguePublic[]>("/v2/leagues", {
        signal,
        elevatedPrivileges,
      });
      const games: GamePublic[] = [
        ...new Map(
          leagues.map((league) => {
            const game: GamePublic = { ...league.game };

            if (league.game.canonical_coworld_id === undefined) {
              delete game.coworld_id;
            } else {
              game.coworld_id = league.game.canonical_coworld_id;
            }

            return [game.id, game] as const;
          }),
        ).values(),
      ].toSorted((a, b) => b.created_at.localeCompare(a.created_at));
      return { games, leagues };
    },
    staleTime: Infinity,
    gcTime: navigationCacheTimeMs,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function roundDetailQueryOptions(roundId: string | null) {
  const elevatedPrivileges = false;
  return queryOptions({
    queryKey: ["rounds", "detail", roundId, { elevatedPrivileges }] as const,
    queryFn:
      roundId === null
        ? skipToken
        : ({ signal }) =>
            apiGet<RoundDetailPublic>(
              `/v2/rounds/${encodeURIComponent(roundId)}`,
              { signal, elevatedPrivileges },
            ),
    staleTime: 30_000,
  });
}

export function roundEpisodesPageQueryOptions(query: RoundEpisodePageQuery) {
  const elevatedPrivileges = false;
  const { roundId, limit, cursor, completedOnly = false } = query;
  return queryOptions({
    queryKey: [
      "rounds",
      roundId,
      "episodes",
      "page",
      { limit, cursor, completedOnly, elevatedPrivileges },
    ] as const,
    queryFn: ({ signal }) =>
      apiGet<RoundEpisodePage>(roundEpisodesPath(query), {
        signal,
        elevatedPrivileges,
      }),
    staleTime: 30_000,
    retry: false,
  });
}

export function divisionFullLeaderboardQueryOptions({
  divisionId,
  includeRecentRounds = false,
}: {
  divisionId: string;
  includeRecentRounds?: boolean;
}) {
  const elevatedPrivileges = false;
  return queryOptions({
    queryKey: [
      "divisions",
      divisionId,
      "leaderboard",
      "full",
      { elevatedPrivileges, includeRecentRounds },
    ] as const,
    queryFn: ({ signal }) =>
      apiGet<LeaderboardEntryPublic[] | null>(
        `/v2/divisions/${encodeURIComponent(divisionId)}/leaderboard${includeRecentRounds ? "" : "?include_recent_rounds=false"}`,
        { signal, elevatedPrivileges },
      ),
    staleTime: 30_000,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
  });
}

export function divisionFirstPlaceQueryOptions(divisionId: string | null) {
  const elevatedPrivileges = false;
  const resource = "first-place";
  return queryOptions({
    queryKey: [
      "divisions",
      divisionId,
      resource,
      { elevatedPrivileges },
    ] as const,
    queryFn:
      divisionId === null
        ? skipToken
        : ({ signal }) =>
            apiGet<FirstPlaceSummaryPublic | null>(
              `/v2/divisions/${encodeURIComponent(divisionId)}/${resource}`,
              { signal, elevatedPrivileges },
            ),
    staleTime: 30_000,
  });
}
